// AWS Amplify v6 compatible auth service
import { Amplify } from 'aws-amplify';
import { 
  signUp, 
  signIn, 
  signOut, 
  confirmSignUp, 
  resendSignUpCode, 
  resetPassword, 
  confirmResetPassword, 
  getCurrentUser,
  fetchUserAttributes, 
  fetchAuthSession ,
  confirmSignIn  // needed for NEW_PASSWORD_REQUIRED
} from 'aws-amplify/auth';

let isConfigured = false;

async function initializeAmplify() {
  if (isConfigured) return true;
  
  // Use environment variables ONLY (skip backend call)
  const region = process.env.VUE_APP_COGNITO_REGION;
  const userPoolId = process.env.VUE_APP_COGNITO_USER_POOL_ID;
  const clientId = process.env.VUE_APP_COGNITO_CLIENT_ID;
  
  console.log('🔧 Loading Cognito config from environment variables:');
  console.log('Region:', region);
  console.log('UserPoolId:', userPoolId);
  console.log('ClientId:', clientId);
  
  if (region && userPoolId && clientId) {
    Amplify.configure({
      Auth: {
        Cognito: {
          region: region,
          userPoolId: userPoolId,
          userPoolClientId: clientId,
        }
      }
    });
    
    isConfigured = true;
    console.log('✅ Amplify configured from environment variables');
    return true;
  }
  
  console.error('❌ No Cognito configuration available in environment variables');
  console.error('Missing:', {
    region: !region,
    userPoolId: !userPoolId,
    clientId: !clientId
  });
  return false;
}

class AuthService {
  constructor() {
    this.user = null;
    this.isAuthenticated = false;
    this.signInResult = null; // Store sign in result for password challenges
  }

  // Sign up new user
  async signUp(email, password, firstName, lastName) {
    const configured = await initializeAmplify();
    if (!configured) {
      return { success: false, error: 'Authentication not configured' };
    }
    
    try {
      const result = await signUp({
        username: email,
        password: password,
        options: {
          userAttributes: {
            email: email,
            given_name: firstName,
            family_name: lastName
          }
        }
      });
      
      console.log('Signup successful:', result);
      return { success: true, user: result };
    } catch (error) {
      console.error('Signup error:', error);
      return { success: false, error: error.message };
    }
  }

  // Confirm signup with verification code
  async confirmSignUp(email, code) {
    try {
      await confirmSignUp({
        username: email,
        confirmationCode: code
      });
      return { success: true };
    } catch (error) {
      console.error('Confirmation error:', error);
      return { success: false, error: error.message };
    }
  }

  // Sign in user
 // UPDATED Sign in user - now handles NEW_PASSWORD_REQUIRED
// Sign in user - handles NEW_PASSWORD_REQUIRED
async signIn(email, password) {
  const configured = await initializeAmplify();
  if (!configured) {
    return { success: false, error: 'Authentication not configured' };
  }
  
  try {
    console.log('🔍 AuthService: Attempting signIn...');
    const result = await signIn({
      username: email,
      password: password
    });
    
    console.log('🔍 AuthService: SignIn result:', result);
    
    // Check if additional steps are needed
    if (result.isSignedIn === false) {
      console.log('🔍 AuthService: isSignedIn is false, checking nextStep...');
      
      // Store the result for completing the challenge
      this.signInResult = result;
      
      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        console.log('🔄 AuthService: NEW_PASSWORD_REQUIRED challenge detected');
        return { 
          success: false, 
          requiresNewPassword: true, 
          error: 'NEW_PASSWORD_REQUIRED' 
        };
      }
      
      // Handle other potential challenges
      console.log('🔍 AuthService: Other challenge type:', result.nextStep?.signInStep);
      return { 
        success: false, 
        error: `Additional step required: ${result.nextStep?.signInStep}` 
      };
    }
    
    // Normal sign in success
    console.log('✅ AuthService: Normal sign in success');
    this.user = result;
    this.isAuthenticated = true;
    this.signInResult = null; // Clear stored result
    
    return { success: true, user: result };
    
  } catch (error) {
    console.error('❌ AuthService: Signin error:', error);
    this.signInResult = null; // Clear on error
    return { success: false, error: error.message };
  }
}

// Complete new password requirement
// Replace the completeNewPassword method in your auth.js with this:

// Complete new password requirement
// Replace your completeNewPassword method in auth.js with this corrected version:

// Complete new password requirement
async completeNewPassword(newPassword, userAttributes = {}) {
  console.log('🔍 AuthService: completeNewPassword called');
  console.log('🔍 AuthService: userAttributes received:', userAttributes);
  
  if (!this.signInResult) {
    console.error('❌ AuthService: No stored signInResult found');
    return { 
      success: false, 
      error: 'No pending password challenge found. Please sign in again.' 
    };
  }

  try {
    console.log('🔍 AuthService: Calling confirmSignIn with new password');
    
    // Check what attributes are missing from the nextStep
    const missingAttributes = this.signInResult.nextStep?.missingAttributes || [];
    console.log('🔍 AuthService: Missing attributes from Cognito:', missingAttributes);
    
    // CRITICAL: Only provide attributes that are actually missing
    // Don't include email or other attributes that might already be set
    const attributesToProvide = {};
    
    // Only add attributes that Cognito says are missing
    if (missingAttributes.includes('given_name')) {
      attributesToProvide.given_name = userAttributes.given_name || 'User';
    }
    if (missingAttributes.includes('family_name')) {
      attributesToProvide.family_name = userAttributes.family_name || 'Name';
    }
    
    // Add any other missing attributes (excluding email which is usually already set)
    missingAttributes.forEach(attr => {
      if (attr !== 'email' && attr !== 'given_name' && attr !== 'family_name') {
        if (userAttributes[attr]) {
          attributesToProvide[attr] = userAttributes[attr];
        }
      }
    });
    
    console.log('🔍 AuthService: Final attributes to provide:', attributesToProvide);
    
    // CRITICAL FIX: In Amplify v6, userAttributes must be inside options object
    const result = await confirmSignIn({
      challengeResponse: newPassword,
      options: {
        userAttributes: attributesToProvide  // ← This was the issue!
      }
    });
    
    console.log('🔍 AuthService: confirmSignIn result:', result);
    
    if (result.isSignedIn) {
      console.log('✅ AuthService: Password change successful, user signed in');
      this.user = result;
      this.isAuthenticated = true;
      this.signInResult = null; // Clear stored result
      
      return { success: true, user: result };
    }
    
    // Handle case where more steps are needed
    console.log('🔍 AuthService: More steps needed after password change');
    return { 
      success: false, 
      error: 'Additional verification required' 
    };
    
  } catch (error) {
    console.error('❌ AuthService: New password completion error:', error);
    
    // Provide more specific error handling
    if (error.message?.includes('given_name is missing')) {
      return { 
        success: false, 
        error: 'User profile information is incomplete. Please contact support.' 
      };
    }
    
    return { success: false, error: error.message };
  }
}
  // Sign out user
  async signOut() {
    try {
      await signOut();
      this.user = null;
      this.isAuthenticated = false;
      this.signInResult = null; // Clear any stored results
      return { success: true };
    } catch (error) {
      console.error('Signout error:', error);
      return { success: false, error: error.message };
    }
  }

  // Get current authenticated user
  async getCurrentUser() {
    const configured = await initializeAmplify();
    if (!configured) {
      return null;
    }
    
    try {
      const user = await getCurrentUser();
      // IMPORTANT: Fetch user attributes separately in Amplify v6
      const attributes = await fetchUserAttributes();
      const userWithAttributes = {
        ...user,
        attributes: attributes
      };
      this.user = userWithAttributes;
      this.isAuthenticated = true;
      return userWithAttributes;
    } catch (error) {
      this.user = null;
      this.isAuthenticated = false;
      return null;
    }
  }

  // Get current user's JWT token
  async getCurrentToken() {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.accessToken?.toString();
    } catch (error) {
      console.error('Token error:', error);
      return null;
    }
  }

  // Check if user is authenticated
  async checkAuthState() {
    try {
      const user = await this.getCurrentUser();
      return !!user;
    } catch (error) {
      return false;
    }
  }

  // Resend confirmation code
  async resendConfirmationCode(email) {
    try {
      await resendSignUpCode({ username: email });
      return { success: true };
    } catch (error) {
      console.error('Resend confirmation error:', error);
      return { success: false, error: error.message };
    }
  }

  // Forgot password
  async forgotPassword(email) {
    try {
      await resetPassword({ username: email });
      return { success: true };
    } catch (error) {
      console.error('Forgot password error:', error);
      return { success: false, error: error.message };
    }
  }

  // Confirm new password
  async forgotPasswordSubmit(email, code, newPassword) {
    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword: newPassword
      });
      return { success: true };
    } catch (error) {
      console.error('Password reset error:', error);
      return { success: false, error: error.message };
    }
  }
}

// Export singleton instance
export const authService = new AuthService();
export default authService;